// ==================== USE WEBSOCKET HOOK ====================
// React hook for WebSocket integration in components

import { useEffect, useRef, useCallback, useState } from 'react';
import { websocketService, type WhatsAppMessagePayload, type MessageStatusUpdatePayload } from '../services/websocket.service';

interface UseWebSocketOptions {
  onMessage?: (message: WhatsAppMessagePayload) => void;
  onStatusUpdate?: (update: MessageStatusUpdatePayload) => void;
  onConnectionChange?: (connected: boolean) => void;
  autoConnect?: boolean;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  joinConversation: (contactId: string) => void;
  leaveConversation: (contactId: string) => void;
  connect: () => void;
  disconnect: () => void;
}

/**
 * Hook for integrating WebSocket real-time updates into components
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { onMessage, onStatusUpdate, onConnectionChange, autoConnect = true } = options;
  
  // Initialize with current connection state (in case already connected)
  const [isConnected, setIsConnected] = useState(() => websocketService.getConnectionState());
  const currentConversationRef = useRef<string | null>(null);

  // Setup message listener
  useEffect(() => {
    if (!onMessage) return;
    
    const unsubscribe = websocketService.onMessage(onMessage);
    return unsubscribe;
  }, [onMessage]);

  // Setup status update listener
  useEffect(() => {
    if (!onStatusUpdate) return;
    
    const unsubscribe = websocketService.onStatusUpdate(onStatusUpdate);
    return unsubscribe;
  }, [onStatusUpdate]);

  // Setup connection listener
  useEffect(() => {
    const handleConnectionChange = (connected: boolean) => {
      setIsConnected(connected);
      onConnectionChange?.(connected);
    };

    // Check current state immediately in case we missed the event
    const currentState = websocketService.getConnectionState();
    setIsConnected(currentState);

    const unsubscribe = websocketService.onConnectionChange(handleConnectionChange);
    return unsubscribe;
  }, [onConnectionChange]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      websocketService.connect();
    }
    
    // Don't disconnect on unmount - keep connection alive for other components
    // Only disconnect when explicitly called or user logs out
  }, [autoConnect]);

  const joinConversation = useCallback((contactId: string) => {
    // Leave previous conversation if any
    if (currentConversationRef.current && currentConversationRef.current !== contactId) {
      websocketService.leaveConversation(currentConversationRef.current);
    }
    
    currentConversationRef.current = contactId;
    websocketService.joinConversation(contactId);
  }, []);

  const leaveConversation = useCallback((contactId: string) => {
    websocketService.leaveConversation(contactId);
    if (currentConversationRef.current === contactId) {
      currentConversationRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    websocketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    // Leave current conversation before disconnecting
    if (currentConversationRef.current) {
      websocketService.leaveConversation(currentConversationRef.current);
      currentConversationRef.current = null;
    }
    websocketService.disconnect();
  }, []);

  return {
    isConnected,
    joinConversation,
    leaveConversation,
    connect,
    disconnect,
  };
}

export default useWebSocket;
